import { Buffer } from 'buffer';
import Moment from 'moment';
import { TranslateService } from '@ngx-translate/core';

export type HorlogeModel = {
  ref: number;
  start: number;
  end: number;
};

export type Weltico2HorlogeModel = {
  ref: number;
  start: number;
  end: number;
  color: number;
};

export type VSHorlogeModel = {
  ref: number;
  start: number;
  end: number;
  vitesse: number;
};

export type WPHorlogeModel = {
  ref: number;
  start: number;
  end: number;
  temperature: number;
};

export type SaltHorlogeModel = {
  ref: number;
  start: number;
  end: number;
  consigne: number;
};

export type TunableHorlogeModel = {
  ref: number;
  start: number;
  end: number;
  typeComportement: number;
  value: any;
};

export function getHorlogeTotalTime(models: HorlogeModel[]) {
  let res = 0;

  for (let i = 0; i < models.length; i++) {
    res += models[i].end - models[i].start;
  }

  if (res < 0) {
    return 24 * 3600 + res;
  }

  return res;
}

export function transformModelToBuffer(models: HorlogeModel[]): string {
  const buf = [
    0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  ];

  for (let i = 0; i < models.length; i++) {
    const end =
      models[i].start >= models[i].end
        ? 24 * 60 * 60 + models[i].end
        : models[i].end;
    for (let j = models[i].start; j < end; j = j + 30 * 60) {
      let index = (j / (30 * 60)) % 48;
      buf[index] = 1;
    }
  }

  const buffer = Buffer.allocUnsafe(6);

  for (let i = 0; i < buf.length; i = i + 8) {
    buffer.writeUInt8(
      buf[i] +
        2 * buf[i + 1] +
        4 * buf[i + 2] +
        8 * buf[i + 3] +
        16 * buf[i + 4] +
        32 * buf[i + 5] +
        64 * buf[i + 6] +
        128 * buf[i + 7],
      i / 8,
    );
  }

  return buffer.toString('hex');
}

export function transformWeltico2ModelToBuffer(
  models: Weltico2HorlogeModel[],
): string {
  const buf = [
    0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  ];

  for (let i = 0; i < models.length; i++) {
    const end =
      models[i].start >= models[i].end
        ? 24 * 60 * 60 + models[i].end
        : models[i].end;
    for (let j = models[i].start; j < end; j = j + 60 * 60) {
      let index = (j / (60 * 60)) % 24;
      buf[index] = models[i].color;
    }
  }

  const buffer = Buffer.allocUnsafe(24);

  for (let i = 0; i < buf.length; i++) {
    buffer.writeUInt8(buf[i], i);
  }

  return buffer.toString('hex');
}

export function transformVSModelToBuffer(models: VSHorlogeModel[]): string {
  const buf = [
    0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  ];

  for (let i = 0; i < models.length; i++) {
    const end =
      models[i].start >= models[i].end
        ? 24 * 60 * 60 + models[i].end
        : models[i].end;
    for (let j = models[i].start; j < end; j = j + 30 * 60) {
      let index = (j / (30 * 60)) % 48;
      buf[index] = models[i].vitesse;
    }
  }

  const buffer = Buffer.allocUnsafe(12);

  for (let i = 0; i < buf.length; i = i + 4) {
    const v = parseInt(
      Number(buf[i + 3])
        .toString(2)
        .padStart(2, '0') +
        Number(buf[i + 2])
          .toString(2)
          .padStart(2, '0') +
        Number(buf[i + 1])
          .toString(2)
          .padStart(2, '0') +
        Number(buf[i]).toString(2).padStart(2, '0'),
      2,
    );
    buffer.writeUInt8(v, i / 4);
  }

  return buffer.toString('hex');
}

export function transformPompeVSModelToBuffer(
  models: VSHorlogeModel[],
): string {
  const buf = [
    0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
    0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  ];

  for (let i = 0; i < models.length; i++) {
    const end =
      models[i].start >= models[i].end
        ? 24 * 60 * 60 + models[i].end
        : models[i].end;
    for (let j = models[i].start; j < end; j = j + 15 * 60) {
      let index = (j / (15 * 60)) % 96;
      buf[index] = models[i].vitesse;
    }
  }

  const buffer = Buffer.allocUnsafe(48);

  for (let i = 0; i < buf.length; i = i + 2) {
    const v = parseInt(
      Number(buf[i + 1])
        .toString(2)
        .padStart(4, '0') + Number(buf[i]).toString(2).padStart(4, '0'),
      2,
    );
    buffer.writeUInt8(v, i / 2);
  }

  return buffer.toString('hex');
}

export function transformWPModelToBuffer(models: WPHorlogeModel[]): string {
  const buf = [
    0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  ];

  for (let i = 0; i < models.length; i++) {
    const end =
      models[i].start >= models[i].end
        ? 24 * 60 * 60 + models[i].end
        : models[i].end;
    for (let j = models[i].start; j < end; j = j + 3600) {
      let index = Math.floor(j / 3600);
      buf[index] = models[i].temperature * 10;
    }
  }

  const buffer = Buffer.allocUnsafe(48);

  for (let i = 0; i < buf.length; i++) {
    buffer.writeUInt16LE(buf[i], i * 2);
  }

  return buffer.toString('hex');
}

export function transformSaltModelToBuffer(models: SaltHorlogeModel[]): string {
  const buf = [
    0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0,
  ];

  for (let i = 0; i < models.length; i++) {
    const end =
      models[i].start >= models[i].end
        ? 24 * 60 * 60 + models[i].end
        : models[i].end;
    for (let j = models[i].start; j < end; j = j + 3600) {
      let index = Math.floor(j / 3600);
      buf[index] = models[i].consigne;
    }
  }

  const buffer = Buffer.allocUnsafe(24);

  for (let i = 0; i < buf.length; i++) {
    buffer.writeUInt8(buf[i], i);
  }

  return buffer.toString('hex');
}

export function transformWeekModelToBuffer(models: HorlogeModel[][]): string {
  let res: string = '';

  const correspondance = [6, 0, 1, 2, 3, 4, 5];

  for (let i = 0; i < 7; i++) {
    res += transformModelToBuffer(models[correspondance[i]]);
  }

  return res;
}

export function transformWeltico2WeekModelToBuffer(
  models: Weltico2HorlogeModel[][],
): string {
  let res: string = '';

  const correspondance = [0, 1, 2, 3, 4, 5, 6];

  for (let i = 0; i < 7; i++) {
    res += transformWeltico2ModelToBuffer(models[correspondance[i]]);
  }

  return res;
}

export function transformVSWeekModelToBuffer(
  models: VSHorlogeModel[][],
): string {
  let res: string = '';

  const correspondance = [6, 0, 1, 2, 3, 4, 5];

  for (let i = 0; i < 7; i++) {
    res += transformVSModelToBuffer(models[correspondance[i]]);
  }

  return res;
}

export function transformPompeVSWeekModelToBuffer(
  models: VSHorlogeModel[][],
): string {
  let res: string = '';

  const correspondance = [0, 1, 2, 3, 4, 5, 6];

  for (let i = 0; i < 7; i++) {
    res += transformPompeVSModelToBuffer(models[correspondance[i]]);
  }

  return res;
}

export function transformWPWeekModelToBuffer(
  models: WPHorlogeModel[][],
): string[] {
  let res: string[] = [];

  for (let i = 0; i < 7; i++) {
    res[i] = transformWPModelToBuffer(models[i]);
  }

  return res;
}

export function transformSaltWeekModelToBuffer(
  models: SaltHorlogeModel[][],
): string[] {
  let res: string[] = [];

  for (let i = 0; i < 7; i++) {
    res[i] = transformSaltModelToBuffer(models[i]);
  }

  return res;
}

export function transformBufferToModel(
  strBuf: string,
  allowCycliqueValue: boolean = true,
): HorlogeModel[] {
  const buffer = Buffer.from(strBuf, 'hex');

  const res: HorlogeModel[] = [];

  let current: HorlogeModel = {
    ref: -1,
    start: -1,
    end: -1,
  };

  let hasCycliqueValue = false;

  if (
    allowCycliqueValue &&
    ((buffer.readUInt8(0) >> 0) & 1) == 1 &&
    ((buffer.readUInt8(5) >> 7) & 1) == 1
  ) {
    hasCycliqueValue = true;
  }

  for (let i = 0; i < 6; i++) {
    const uint = buffer.readUInt8(i);

    for (let j = 0; j < 8; j++) {
      const v = (uint >> j) & 1;

      if (v == 1 && current.start == -1) {
        current.start = (i * 8 + j) * 30 * 60;
      } else if (v == 0 && current.start != -1 && current.end == -1) {
        current.end = (i * 8 + j) * 30 * 60;
        current.ref = res.length + 1;
        res.push(current);
        current = {
          ref: -1,
          start: -1,
          end: -1,
        };
      }
    }
  }

  if (current.start != -1) {
    if (hasCycliqueValue == true && res.length != 0) {
      res[0].start = current.start;
    } else {
      current.end = 24 * 60 * 60;
      current.ref = res.length + 1;
      res.push(current);
    }
  }

  return res;
}

export function transformWeltico2BufferToModel(
  strBuf: string,
  allowCycliqueValue: boolean = true,
): Weltico2HorlogeModel[] {
  const buffer = Buffer.from(strBuf, 'hex');

  const res: Weltico2HorlogeModel[] = [];

  let current: Weltico2HorlogeModel = {
    ref: -1,
    start: -1,
    end: -1,
    color: -1,
  };

  let hasCycliqueValue = false;

  if (
    allowCycliqueValue &&
    ((buffer.readUInt8(0) >> 0) & 1) != 0 &&
    buffer.readUInt8(0) === buffer.readUInt8(23)
  ) {
    hasCycliqueValue = true;
  }

  for (let i = 0; i < 24; i++) {
    const uint = buffer.readUInt8(i);

    const color = uint;
    if (color > 0 && current.start == -1) {
      current.start = i * 60 * 60;
      current.color = color;
    } else if (color == 0 && current.start != -1 && current.end == -1) {
      current.end = i * 60 * 60;
      current.ref = res.length + 1;
      res.push(current);
      current = {
        ref: -1,
        start: -1,
        end: -1,
        color: -1,
      };
    } else if (
      color > 0 &&
      current.color !== color &&
      current.start != -1 &&
      current.end == -1
    ) {
      current.end = i * 60 * 60;
      current.ref = res.length + 1;
      res.push(current);
      current = {
        ref: -1,
        start: i * 60 * 60,
        end: -1,
        color: color,
      };
    }
  }

  if (current.start != -1) {
    if (hasCycliqueValue == true && res.length != 0) {
      res[0].start = current.start;
    } else {
      current.end = 24 * 60 * 60;
      current.ref = res.length + 1;
      res.push(current);
    }
  }

  return res;
}

export function transformVSBufferToModel(
  strBuf: string,
  allowCycliqueValue: boolean = true,
): VSHorlogeModel[] {
  const buffer = Buffer.from(strBuf, 'hex');

  const res: VSHorlogeModel[] = [];

  let current: VSHorlogeModel = {
    ref: -1,
    start: -1,
    end: -1,
    vitesse: -1,
  };

  let hasCycliqueValue = false;

  if (
    allowCycliqueValue &&
    ((buffer.readUInt8(0) >> 0) & 0x03) > 0 &&
    ((buffer.readUInt8(0) >> 0) & 0x03) === ((buffer.readUInt8(11) >> 6) & 0x03)
  ) {
    hasCycliqueValue = true;
  }

  for (let i = 0; i < 12; i++) {
    const uint = buffer.readUInt8(i);

    for (let j = 0; j < 8; j += 2) {
      const v = (uint >> j) & 0x03;

      if (v > 0 && current.start == -1) {
        current.start = (i * 4 + j / 2) * 30 * 60;
        current.vitesse = v;
      } else if (v == 0 && current.start != -1 && current.end == -1) {
        current.end = (i * 4 + j / 2) * 30 * 60;
        current.ref = res.length + 1;
        res.push(current);
        current = {
          ref: -1,
          start: -1,
          end: -1,
          vitesse: -1,
        };
      } else if (
        v > 0 &&
        current.vitesse !== v &&
        current.start != -1 &&
        current.end == -1
      ) {
        current.end = (i * 4 + j / 2) * 30 * 60;
        current.ref = res.length + 1;
        res.push(current);
        current = {
          ref: -1,
          start: (i * 4 + j / 2) * 30 * 60,
          end: -1,
          vitesse: v,
        };
      }
    }
  }

  if (current.start != -1) {
    if (hasCycliqueValue == true && res.length != 0) {
      res[0].start = current.start;
    } else {
      current.end = 24 * 60 * 60;
      current.ref = res.length + 1;
      res.push(current);
    }
  }

  return res;
}

export function transformPompeVSBufferToModel(
  strBuf: string,
  allowCycliqueValue: boolean = true,
): VSHorlogeModel[] {
  const buffer = Buffer.from(strBuf, 'hex');

  const res: VSHorlogeModel[] = [];

  let current: VSHorlogeModel = {
    ref: -1,
    start: -1,
    end: -1,
    vitesse: -1,
  };

  let hasCycliqueValue = false;

  if (
    allowCycliqueValue &&
    ((buffer.readUInt8(0) >> 0) & 0x0f) > 0 &&
    ((buffer.readUInt8(0) >> 0) & 0x0f) === ((buffer.readUInt8(47) >> 4) & 0x0f)
  ) {
    hasCycliqueValue = true;
  }

  for (let i = 0; i < 48; i++) {
    const uint = buffer.readUInt8(i);

    for (let j = 0; j < 8; j += 4) {
      const v = (uint >> j) & 0x0f;

      if (v > 0 && current.start == -1) {
        current.start = (i * 2 + j / 4) * 15 * 60;
        current.vitesse = v;
      } else if (v == 0 && current.start != -1 && current.end == -1) {
        current.end = (i * 2 + j / 4) * 15 * 60;
        current.ref = res.length + 1;
        res.push(current);
        current = {
          ref: -1,
          start: -1,
          end: -1,
          vitesse: -1,
        };
      } else if (
        v > 0 &&
        current.vitesse !== v &&
        current.start != -1 &&
        current.end == -1
      ) {
        current.end = (i * 2 + j / 4) * 15 * 60;
        current.ref = res.length + 1;
        res.push(current);
        current = {
          ref: -1,
          start: (i * 2 + j / 4) * 15 * 60,
          end: -1,
          vitesse: v,
        };
      }
    }
  }

  if (current.start != -1) {
    if (hasCycliqueValue == true && res.length != 0) {
      res[0].start = current.start;
    } else {
      current.end = 24 * 60 * 60;
      current.ref = res.length + 1;
      res.push(current);
    }
  }

  return res;
}

export function transformWPBufferToModel(
  strBuf: string,
  allowCycliqueValue: boolean = true,
): WPHorlogeModel[] {
  const buffer = Buffer.from(strBuf, 'hex');

  const res: WPHorlogeModel[] = [];

  let current: WPHorlogeModel = {
    ref: -1,
    start: -1,
    end: -1,
    temperature: 0,
  };

  let hasCycliqueValue = false;

  if (allowCycliqueValue && buffer.readUInt16LE(0) == buffer.readUInt16LE(23)) {
    hasCycliqueValue = true;
  }

  for (let i = 0; i < 24; i++) {
    const uint = buffer.readUInt16LE(i * 2) / 10;

    if (uint > 0 && current.start == -1) {
      current.start = i * 3600;
      current.temperature = uint;
    } else if (uint == 0 && current.start != -1 && current.end == -1) {
      current.end = i * 3600;
      current.ref = res.length + 1;
      res.push(current);
      current = {
        ref: -1,
        start: -1,
        end: -1,
        temperature: 0,
      };
    } else if (
      uint > 0 &&
      current.temperature !== uint &&
      current.start != -1 &&
      current.end == -1
    ) {
      current.end = i * 3600;
      current.ref = res.length + 1;
      res.push(current);
      current = {
        ref: -1,
        start: i * 3600,
        end: -1,
        temperature: uint,
      };
    }
  }

  if (current.start != -1) {
    if (hasCycliqueValue == true && res.length != 0) {
      res[0].start = current.start;
    } else {
      current.end = 24 * 60 * 60;
      current.ref = res.length + 1;
      res.push(current);
    }
  }

  return res;
}

export function transformSaltBufferToModel(
  strBuf: string,
  allowCycliqueValue: boolean = true,
): SaltHorlogeModel[] {
  const buffer = Buffer.from(strBuf, 'hex');

  const res: SaltHorlogeModel[] = [];

  let current: SaltHorlogeModel = {
    ref: -1,
    start: -1,
    end: -1,
    consigne: 0,
  };

  let hasCycliqueValue = false;

  if (allowCycliqueValue && buffer.readUInt8(0) == buffer.readUInt8(23)) {
    hasCycliqueValue = true;
  }

  for (let i = 0; i < 24; i++) {
    const uint = buffer.readUInt8(i);

    if (uint > 0 && current.start == -1) {
      current.start = i * 3600;
      current.consigne = uint;
    } else if (uint == 0 && current.start != -1 && current.end == -1) {
      current.end = i * 3600;
      current.ref = res.length + 1;
      res.push(current);
      current = {
        ref: -1,
        start: -1,
        end: -1,
        consigne: 0,
      };
    } else if (
      uint > 0 &&
      current.consigne !== uint &&
      current.start != -1 &&
      current.end == -1
    ) {
      current.end = i * 3600;
      current.ref = res.length + 1;
      res.push(current);
      current = {
        ref: -1,
        start: i * 3600,
        end: -1,
        consigne: uint,
      };
    }
  }

  if (current.start != -1) {
    if (hasCycliqueValue == true && res.length != 0) {
      res[0].start = current.start;
    } else {
      current.end = 24 * 60 * 60;
      current.ref = res.length + 1;
      res.push(current);
    }
  }

  return res;
}

export function transformVSWeekBufferToModel(
  strBuf: string,
): VSHorlogeModel[][] {
  const res: VSHorlogeModel[][] = [[], [], [], [], [], [], []];

  const correspondance = [6, 0, 1, 2, 3, 4, 5];

  if (strBuf.length != 6 * 7 * 2 * 2) {
    return res;
  }

  let j = 0;
  for (let i = 0; i < 6 * 7 * 2 * 2; i = i + 6 * 2 * 2) {
    let str = strBuf.substr(i, 6 * 2 * 2);
    res[correspondance[j]] = transformVSBufferToModel(str, false);
    j = j + 1;
  }

  return res;
}

export function transformPompeVSWeekBufferToModel(
  strBuf: string,
): VSHorlogeModel[][] {
  const res: VSHorlogeModel[][] = [[], [], [], [], [], [], []];

  const correspondance = [0, 1, 2, 3, 4, 5, 6];

  if (strBuf.length != 48 * 7 * 2) {
    return res;
  }

  let j = 0;
  for (let i = 0; i < 48 * 7 * 2; i = i + 48 * 2) {
    let str = strBuf.substr(i, 48 * 2);
    res[correspondance[j]] = transformPompeVSBufferToModel(str, false);
    j = j + 1;
  }

  return res;
}

export function transformWPWeekBufferToModel(
  strBuf: string[],
): WPHorlogeModel[][] {
  const res: WPHorlogeModel[][] = [[], [], [], [], [], [], []];

  if (strBuf.length != 7) {
    return res;
  }

  for (let i = 0; i < 7; i++) {
    res[i] = transformWPBufferToModel(strBuf[i], false);
  }

  return res;
}

export function transformSaltWeekBufferToModel(
  strBuf: string[],
): SaltHorlogeModel[][] {
  const res: SaltHorlogeModel[][] = [[], [], [], [], [], [], []];

  if (strBuf.length != 7) {
    return res;
  }

  for (let i = 0; i < 7; i++) {
    res[i] = transformSaltBufferToModel(strBuf[i], false);
  }

  return res;
}

export function transformWeekBufferToModel(strBuf: string): HorlogeModel[][] {
  const res: HorlogeModel[][] = [[], [], [], [], [], [], []];

  const correspondance = [6, 0, 1, 2, 3, 4, 5];

  if (strBuf.length != 6 * 7 * 2) {
    return res;
  }

  let j = 0;
  for (let i = 0; i < 6 * 7 * 2; i = i + 6 * 2) {
    let str = strBuf.substr(i, 6 * 2);
    res[correspondance[j]] = transformBufferToModel(str, false);
    j = j + 1;
  }

  return res;
}

export function transformWelticoWeekBufferToModel(
  strBuf: string,
): Weltico2HorlogeModel[][] {
  const res: Weltico2HorlogeModel[][] = [[], [], [], [], [], [], []];

  const correspondance = [0, 1, 2, 3, 4, 5, 6];

  if (strBuf.length != 24 * 7 * 2) {
    return res;
  }

  let j = 0;
  for (let i = 0; i < 24 * 7 * 2; i = i + 24 * 2) {
    let str = strBuf.substr(i, 24 * 2);
    res[correspondance[j]] = transformWeltico2BufferToModel(str, false);
    j = j + 1;
  }

  return res;
}

export function getAllMonth(t: any): any[] {
  const itemsMonth: any[] = [
    { label: t('month.jan'), value: 0 },
    { label: t('month.feb'), value: 1 },
    { label: t('month.mar'), value: 2 },
    { label: t('month.apr'), value: 3 },
    { label: t('month.may'), value: 4 },
    { label: t('month.jun'), value: 5 },
    { label: t('month.jul'), value: 6 },
    { label: t('month.aug'), value: 7 },
    { label: t('month.sep'), value: 8 },
    { label: t('month.oct'), value: 9 },
    { label: t('month.nov'), value: 10 },
    { label: t('month.dec'), value: 11 },
  ];

  return itemsMonth;
}

export function getAllYears(): any[] {
  const itemsYear: any[] = [];

  for (let i = 2021; i <= Moment().year(); i++) {
    itemsYear.push({ label: i + '', value: i });
  }

  return itemsYear;
}

export function transformHourToDayHour(
  value: number,
  translateService: TranslateService,
) {
  if (value >= 24) {
    return (
      Math.floor(value / 24) +
      translateService.instant('time.day') +
      ' ' +
      (value % 24) +
      translateService.instant('time.hour')
    );
  }

  return value + translateService.instant('time.hour');
}
